// ============================================================================
// SUPABASE CLIENT - STORE APP
// ============================================================================

import { createClient } from '@supabase/supabase-js';
import { patchSessionGuard } from '@socialflows/shared-lib';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:54321';
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '';

export const supabase = createClient(supabaseUrl, supabasePublishableKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

patchSessionGuard(supabase);

// Get current session
export async function getSession() {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();
  return { session, error };
}

// Get current user
export async function getUser() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  return { user, error };
}

// Sign in with email and password
export async function signIn(email: string, password: string) {
  return await supabase.auth.signInWithPassword({
    email,
    password,
  });
}

// Sign up new user
export async function signUp(email: string, password: string, metadata?: Record<string, unknown>) {
  return await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata,
    },
  });
}

// Sign out
export async function signOut() {
  return await supabase.auth.signOut();
}

// Get user profile from users table
export async function getUserProfile(userId: string) {
  const { data, error } = await supabase.from('users').select('*').eq('id', userId).single();

  return { data, error };
}

// Get account by hostname (for multi-tenancy)
export async function getAccountByHostname(hostname: string) {
  const { data, error } = await supabase
    .from('accounts')
    .select('*')
    .eq('hostname', hostname)
    .maybeSingle();

  return { data, error };
}

// Get public products
export async function getPublicProducts(filters?: {
  categoryId?: string;
  brandId?: string;
  isVisible?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  let query = supabase
    .from('products')
    .select('*, product_variants(*), categories(*), brands(*)', { count: 'exact' })
    .eq('is_visible', true)
    .eq('status', 'ACTIVE');

  if (filters?.categoryId) query = query.eq('category_id', filters.categoryId);
  if (filters?.brandId) query = query.eq('brand_id', filters.brandId);
  if (filters?.search) query = query.ilike('label', `%${filters.search}%`);
  if (filters?.limit) query = query.limit(filters.limit);
  if (filters?.offset !== undefined && filters?.limit)
    query = query.range(filters.offset, filters.offset + filters.limit - 1);

  query = query.order('created_at', { ascending: false });

  return await query;
}

// Real-time subscription helper
export function subscribeToTable(
  table: string,
  callback: (payload: Record<string, unknown>) => void,
  filter?: { column: string; value: string }
) {
  const channelName = `${table}-changes`;

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: table as string,
        filter: filter ? `${filter.column}=eq.${filter.value}` : undefined,
      },
      callback
    )
    .subscribe();

  return channel;
}

// Export default client
export default supabase;
